import React, { useEffect, useState } from 'react';
import styles from './LoginPage.module.scss';
import { QueryFactory } from 'services/api';
import { useScopedTranslation } from 'application/localization/useScopedTranslation';
import clsx from 'clsx';
import {
  Button,
  ButtonColor,
  ButtonWidth,
} from 'components/uikit/buttons/Button';
import { Field } from 'components/uikit/Field';
import { Input } from 'components/uikit/inputs/Input';
import { useAdvancedForm } from 'helpers/form/useAdvancedForm';
import { requiredRule } from 'helpers/form/react-hook-form-helper';
import { FormError } from 'components/uikit/FormError';
import { Loading } from 'components/uikit/suspense/Loading';
import { handleLoginErrors, sendLoginRequest } from 'helpers/auth/auth-client';
import { queryClient } from 'services/api/query-client-helper';
import { errorToString } from 'helpers/error-helpers';
import { UnauthorizedPageRoot } from './UnauthorizedPageRoot';
import { Links } from 'application/constants/links';
import { useNavigate } from 'react-router';
import { openExternalLoginPopup } from '../../helpers/auth/openid/openid-manager.ts';
import AzureIcon from 'assets/img/identity-221-Azure-Active-Directory.svg';
import { FeatureFlags } from '../../application/constants/env-variables.ts';
import { AzureAd } from '../authorized/rootPage/RootPage.tsx';
import { logOut } from '../../helpers/auth/auth-interceptor.ts';

type Form = {
  login: string;
  password: string;
};

export const LoginPage: React.FC = (props) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const serverVersionQuery = QueryFactory.VersionQuery.useVersionQuery({
    throwOnError: false,
  });
  const i18n = useScopedTranslation('Page.Login');
  const form = useAdvancedForm<Form>(async (data) => {
    setIsLoading(true);
    try {
      await sendLoginRequest(data.login.trim(), data.password.trim());
      await queryClient.resetQueries();
    } catch (e) {
      handleLoginErrors(e, i18n);
    } finally {
      setIsLoading(false);
    }
  });

  useEffect(() => {
    void navigate('/');
  }, []);

  return (
    <UnauthorizedPageRoot>
      <Loading loading={isLoading} testId="login-container">
        <form onSubmit={form.handleSubmitDefault}>
          <div className={clsx(styles.loginTitle, styles.loginTitlePadding)}>
            {i18n.t('login_header')}
          </div>
          <Field
            title={i18n.t('login_field')}
            className={styles.inputField}
            testId="Login"
          >
            <Input
              {...form.register('login', { ...requiredRule() })}
              errorText={form.formState.errors.login?.message}
              fullWidth
            />
          </Field>

          <Field
            title={i18n.t('password_field')}
            className={styles.inputField}
            testId="Password"
            linkProps={{
              className: styles.forgotPassword,
              title: i18n.t('forgot_password_button'),
              onClick: () =>
                void navigate(Links.Unauthorized.ForgotPassword.link()),
            }}
          >
            <Input
              {...form.register('password', { ...requiredRule() })}
              type="password"
              errorText={form.formState.errors.password?.message}
              fullWidth
            />
          </Field>
          <FormError>
            {form.overallError
              ? i18n.t(form.overallError.toLowerCase() as any, {
                  defaultValue: form.overallError,
                })
              : null}
          </FormError>
          <div className={styles.buttonContainer}>
            <Button
              title={i18n.t('login_button')}
              type="submit"
              width={ButtonWidth.Fullwidth}
              testId={'login-button'}
            />
            {FeatureFlags.isAzureAuthEnabled() && (
              <>
                <label className={styles.loginSeparator}>{i18n.t('or')}</label>
                <Button
                  color={ButtonColor.External}
                  width={ButtonWidth.Fullwidth}
                  icon={AzureIcon}
                  iconClassName={styles.icon}
                  title={i18n.t('login_aad')}
                  onClick={async () => {
                    try {
                      setIsLoading(true);
                      form.setOverallError('');
                      await openExternalLoginPopup(AzureAd);
                    } catch (e) {
                      if (errorToString(e) === 'multiple_users_found') {
                        form.setOverallError(i18n.t('Multiple_Users_Found'));
                        await logOut();
                      } else if (errorToString(e) === 'user_is_locked') {
                        form.setOverallError(i18n.t('Login_User_Locked'));
                        await logOut();
                      }
                    } finally {
                      setIsLoading(false);
                    }
                  }}
                />
              </>
            )}
          </div>
        </form>
      </Loading>
    </UnauthorizedPageRoot>
  );
};
